/**
 * Preset caption styles for the karaoke subtitle overlay.
 * Values are platform-agnostic (plain colors/numbers) so both
 * web (CSS) and mobile (React Native StyleSheet) can consume them.
 */
export const SUBTITLE_PRESETS = {
  cinema: {
    label: "Cinema",
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    textColor: "#ffffff",
    activeTextColor: "#fde68a",
    fontSize: 20,
    mobileFontSize: 30,
    fontFamily: "Georgia, serif",
    // Gelasio Bold (not Regular) — RN drops fontWeight for custom fonts, so the face carries it.
    mobileFontFamily: "Gelasio-Bold",
    fontWeight: "700",
    textShadow: false,
    textOutline: false,
    outlineColor: "white",
  },
  neon: {
    label: "Neon",
    backgroundColor: "rgba(10, 0, 30, 0.85)",
    textColor: "rgba(255,255,255,0.55)",
    activeTextColor: "#38bdf8",
    fontSize: 20,
    mobileFontSize: 30,
    fontFamily: "'Courier New', monospace",
    // Courier New itself isn't bundled or available on Android — Courier Prime is the standard
    // open-source substitute, same reasoning as Gelasio above.
    mobileFontFamily: "CourierPrime-Bold",
    fontWeight: "700",
    textShadow: false,
    textOutline: false,
    outlineColor: "white",
  },
  clean: {
    label: "Clean",
    backgroundColor: "transparent",
    textColor: "rgba(255,255,255,0.9)",
    activeTextColor: "#ffcc00",
    fontSize: 20,
    mobileFontSize: 31,
    // Kumbh Sans Bold — same family as "soft" but a genuinely different face (700 vs 900).
    fontFamily: "'Kumbh Sans', sans-serif",
    mobileFontFamily: "KumbhSans-Bold",
    fontWeight: "700",
    textShadow: true,
    textOutline: true,
    outlineColor: "white",
  },
  bold: {
    label: "Bold",
    backgroundColor: "rgba(0, 0, 0, 0.92)",
    textColor: "#facc15",
    activeTextColor: "#ffffff",
    fontSize: 20,
    mobileFontSize: 30,
    // Same real Anton on both platforms — Arial Black/Impact have no open metric-compatible clone.
    fontFamily: "'Anton', sans-serif",
    mobileFontFamily: "Anton-Regular",
    fontWeight: "800",
    textShadow: false,
    textOutline: false,
    outlineColor: "white",
  },
  soft: {
    label: "Soft",
    backgroundColor: "transparent",
    textColor: "rgb(255, 255, 255)",
    activeTextColor: "#7c3aed",
    fontSize: 20,
    mobileFontSize: 30,
    fontFamily: "'Kumbh Sans', sans-serif",
    // Weight is baked into this face, not applied via RN's unreliable custom-font fontWeight.
    mobileFontFamily: "KumbhSans-Black",
    fontWeight: "900",
    textShadow: true,
    textOutline: true,
    outlineColor: "white",
  },
  // Default preset for both grammar-response video and introduction-video captions.
  responseContrast: {
    label: "Contrast",
    backgroundColor: "transparent",
    textColor: "#ffffff",
    activeTextColor: "#ffcc00",
    fontSize: 20,
    mobileFontSize: 31,
    fontFamily: "'Anton', sans-serif",
    mobileFontFamily: "Anton-Regular",
    fontWeight: "400",
    textShadow: false,
    textOutline: true,
    outlineColor: "black",
  },
} as const;

export type SubtitlePreset = keyof typeof SUBTITLE_PRESETS;
export type SubtitlePosition = "top" | "center" | "bottom";

export interface SubtitleWord {
  word: string;
  start: number;
  end: number;
  cueIndex?: number | null;
}

const PAUSE_THRESHOLD = 0.8;

/**
 * Groups subtitle words into sentence-level cues.
 * Uses Whisper's cueIndex when available (accurate NLP segmentation),
 * otherwise falls back to gap-based splitting for older data without cueIndex.
 */
export function computeCues(words: SubtitleWord[]): SubtitleWord[][] {
  if (words.length === 0) return [];

  const hasCueIndex = words.some((w) => w.cueIndex != null);

  if (hasCueIndex) {
    const map = new Map<number, SubtitleWord[]>();
    for (const word of words) {
      const idx = word.cueIndex ?? 0;
      const group = map.get(idx) ?? [];
      group.push(word);
      map.set(idx, group);
    }
    return Array.from(map.values()).sort((a, b) => (a[0]?.start ?? 0) - (b[0]?.start ?? 0));
  }

  // Fallback: split on gaps > 0.8s for data generated before cueIndex was added
  const cues: SubtitleWord[][] = [];
  let current: SubtitleWord[] = [];
  let prevEnd = 0;
  for (const word of words) {
    if (current.length > 0 && word.start - prevEnd > PAUSE_THRESHOLD) {
      cues.push(current);
      current = [];
    }
    current.push(word);
    prevEnd = word.end;
  }
  if (current.length > 0) cues.push(current);
  return cues;
}

/**
 * True when a cue is one word broken into syllables rather than a run of separate words:
 * its parts concatenate (ignoring spaces) back into the full word carried by the first cue.
 * Pronunciation slides author subtitles this way.
 */
export function isSyllableCue(cue: SubtitleWord[], fullWordFromFirstCue: string): boolean {
  if (cue.length <= 1 || fullWordFromFirstCue === "") return false;
  if (cue.some((w) => w.word.includes(" "))) return false;

  return (
    cue
      .map((w) => w.word)
      .join("")
      .toLowerCase() === fullWordFromFirstCue.replace(/ /g, "").toLowerCase()
  );
}

/** True when the subtitles belong to a pronunciation slide's syllable breakdown. */
export function isSyllableSubtitleSet(cues: SubtitleWord[][]): boolean {
  const fullWord = cues[0]?.[0]?.word ?? "";
  return cues.some((cue) => isSyllableCue(cue, fullWord));
}

/**
 * How long before its first word a cue appears, in seconds.
 *
 * Applies to a pronunciation slide's syllable breakdown: the student needs a moment to see the
 * word on screen before the first syllable is spoken, or the highlight has already moved on by
 * the time they find it. This does NOT shift the per-word highlight, which stays locked to the
 * audio — during the lead-in the text is up but nothing is lit yet.
 *
 * Ordinary sentence captions are excluded and appear exactly on their first word.
 */
export const SUBTITLE_LEAD_IN_SECONDS = 0.3;

/**
 * The time a cue should become visible: its first word's start, brought forward by the
 * lead-in, never past the previous cue's end so two cues can't be on screen at once.
 */
export function getCueAppearTime(cue: SubtitleWord[], previousCue?: SubtitleWord[]): number {
  const firstStart = cue[0]?.start ?? 0;
  const earliest = previousCue?.at(-1)?.end ?? 0;
  return Math.max(earliest, firstStart - SUBTITLE_LEAD_IN_SECONDS);
}

/**
 * Finds the cue to show at `currentTime`.
 *
 * Whether the lead-in applies is decided here from the subtitles themselves rather than passed
 * in: every surface showing these captions wants the same answer, and a flag at each call site
 * is a flag that can be wired up backwards on one of them.
 */
export function findVisibleCue(cues: SubtitleWord[][], currentTime: number): SubtitleWord[] | null {
  const applyLeadIn = isSyllableSubtitleSet(cues);

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i];
    if (!cue || cue.length === 0) continue;

    const from = applyLeadIn ? getCueAppearTime(cue, cues[i - 1]) : (cue[0]?.start ?? 0);
    const until = cue.at(-1)?.end ?? 0;

    if (currentTime >= from && currentTime <= until) return cue;
  }
  return null;
}

export const DEFAULT_SUBTITLE_PRESET: SubtitlePreset = "responseContrast";
export const DEFAULT_SUBTITLE_POSITION: SubtitlePosition = "top";

/**
 * Caption size is stored as a multiplier of the preset's own fontSize rather than an absolute
 * size — an absolute px value authored against a desktop preview would be unreadable on a phone.
 */
export const DEFAULT_SUBTITLE_FONT_SCALE = 2.0;
export const SUBTITLE_FONT_SCALE_MIN = 0.5;
export const SUBTITLE_FONT_SCALE_MAX = 2.5;

/** The web preview's rendered video width the preset fontSize values were tuned against. */
export const SUBTITLE_FONT_SIZE_REFERENCE_WIDTH = 354;

/**
 * Caption side margin, as a percentage of the video's rendered width (16px at the reference
 * width above) — a fixed px margin eats a bigger share of a narrow video and a smaller share of
 * a wide one, so at matching font sizes the two platforms would still wrap text differently
 * whenever their rendered widths differ, same reasoning as CAPTION_EDGE_INSET_PERCENT.
 */
export const CAPTION_HORIZONTAL_INSET_PERCENT = (16 / SUBTITLE_FONT_SIZE_REFERENCE_WIDTH) * 100;

/**
 * Caption bubble's own horizontal padding (14px at the reference width above), as a percentage
 * of the video's rendered width — applied unconditionally on both platforms, even when the
 * bubble's backgroundColor is transparent: an invisible-background bubble still uses this
 * padding to narrow the width text wraps against, same as a visible one.
 */
export const CAPTION_BUBBLE_PADDING_PERCENT = (14 / SUBTITLE_FONT_SIZE_REFERENCE_WIDTH) * 100;

/**
 * Caption font size as a percentage of the video's own rendered width, so the same authored
 * size looks the same relative to the video on any screen or preview box — matching how
 * CAPTION_EDGE_INSET_PERCENT already does this for vertical position.
 */
export function getSubtitleFontSize(
  baseFontSize: number,
  fontScale: number,
  renderedWidth: number
): number {
  return Math.round(
    (baseFontSize / SUBTITLE_FONT_SIZE_REFERENCE_WIDTH) * renderedWidth * fontScale
  );
}

/** Mobile's bundled caption font renders bigger than the web font at the same nominal size, so mobile call sites multiply getSubtitleFontSize's result by this to match web's actual rendered size. */
export const MOBILE_FONT_METRICS_CORRECTION = 1;

/** Web's WebkitTextStroke is a flat 1.5px at fontSize 36 (VideoPlayer.tsx) — this is that same ratio, so mobile's stroke stays proportional to its own font size instead of using a fixed px that looks thicker or thinner as the font size changes with video width. */
const WEB_TEXT_STROKE_RATIO = 1.5 / 36;

const UNIT_STROKE_OFFSETS: [number, number][] = [
  [1, 0],
  [Math.SQRT1_2, Math.SQRT1_2],
  [0, 1],
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-1, 0],
  [-Math.SQRT1_2, -Math.SQRT1_2],
  [0, -1],
  [Math.SQRT1_2, -Math.SQRT1_2],
];

/** Mobile fakes a text outline (no native text-stroke in RN) by drawing the caption 8 times at pixel offsets scaled to match web's actual stroke-to-font-size ratio. */
export function getSubtitleStrokeOffsets(fontSize: number): [number, number][] {
  const strokeWidth = fontSize * WEB_TEXT_STROKE_RATIO;
  return UNIT_STROKE_OFFSETS.map(([dx, dy]) => [dx * strokeWidth, dy * strokeWidth]);
}

// Grammar-response and introduction-video captions share one set of defaults. These aliases
// remain so the grammar-response call sites keep naming the surface they belong to.
export const DEFAULT_RESPONSE_SUBTITLE_PRESET: SubtitlePreset = DEFAULT_SUBTITLE_PRESET;
export const DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE = DEFAULT_SUBTITLE_FONT_SCALE;

/**
 * Vertical nudge away from the anchor chosen by SubtitlePosition, as a percentage of the
 * player's height, positive downward. Percentage rather than pixels for the same reason.
 */
export const DEFAULT_SUBTITLE_OFFSET = 0;
export const SUBTITLE_OFFSET_MIN = -40;
export const SUBTITLE_OFFSET_MAX = 40;

/**
 * Base clearance from the player's top/bottom edge for a "top"/"bottom" anchored caption,
 * as a percentage of the player's height — same unit as the offset nudge above, and for the
 * same reason: the web preview's box and a phone's screen are wildly different absolute sizes,
 * so a flat px/dp clearance would land at a different relative height on each. Expressing it
 * as a percentage is what makes a "bottom" caption sit at the same relative spot on both.
 */
export const CAPTION_EDGE_INSET_PERCENT = { top: 3, bottom: 15 };

/**
 * Caption line height, as a multiple of font size — set explicitly rather than left to each
 * platform's default (CSS `line-height: normal` on web, the native default on mobile), because
 * that default comes from each font FILE's own internal metrics. Web and mobile use different
 * font files for the same preset (fontFamily vs mobileFontFamily), so their defaults don't
 * necessarily agree even when the font size itself is scaled to match — a multi-line caption
 * would end up a different total height on each even at an otherwise "matching" size.
 */
export const CAPTION_LINE_HEIGHT_RATIO = 1.3;

export function clampSubtitleFontScale(
  value: number | null | undefined,
  defaultValue: number = DEFAULT_SUBTITLE_FONT_SCALE
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return defaultValue;
  return Math.min(SUBTITLE_FONT_SCALE_MAX, Math.max(SUBTITLE_FONT_SCALE_MIN, value));
}

export function clampSubtitleOffset(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_SUBTITLE_OFFSET;
  return Math.min(SUBTITLE_OFFSET_MAX, Math.max(SUBTITLE_OFFSET_MIN, value));
}

export type SyllableSegment = {
  text: string;
  /** index into the cue's word array. -1 for a word-boundary space. */
  sylIdx: number;
};

/**
 * reconstructs the full word display from individual syllables, inserting
 * word-boundary spaces in the correct positions.
 *
 * Example: fullWord="trash cans", syllables=["tr","a","sh","c","a","ns"]
 * → [("tr",0),("a",1),("sh",2),(" ",-1),("c",3),("a",4),("ns",5)]
 *
 * render each segment as an inline span (no gap) to form a continuous word
 * where only the active syllable is highlighted.
 */
export function buildSyllableSegments(fullWord: string, syllables: string[]): SyllableSegment[] {
  // find which positions (in the no-space char stream) need a space before them
  const spaceBeforePos = new Set<number>();
  let noSpacePos = 0;
  for (const ch of fullWord) {
    if (ch === " ") {
      spaceBeforePos.add(noSpacePos);
    } else {
      noSpacePos++;
    }
  }

  const result: SyllableSegment[] = [];
  let pos = 0;
  for (let i = 0; i < syllables.length; i++) {
    if (spaceBeforePos.has(pos)) {
      result.push({ text: " ", sylIdx: -1 });
    }
    result.push({ text: syllables[i] ?? "", sylIdx: i });
    pos += (syllables[i] ?? "").length;
  }
  return result;
}
